import React, { useMemo, useState } from 'react';
import { ChoiceGroup, ChoiceGroupItem } from '@fiverr-private/forms';
import { Typography } from '@fiverr-private/typography';
import { Stack } from '@fiverr-private/layout_components';
import { Alert, AlertText } from '@fiverr-private/feedback';
import { Gql } from '@fiverr-private/dynamic_matching';
import { EditComponentProps } from '..';
import { EditActions } from '../EditActions';
import { AppI18n, appTranslate } from '../../../i18n';
import { useBriefCreationContext } from '../../../context/BriefCreationContext';
import { BriefNeedType } from './types';
import { NeedTypeOption } from './NeedTypeOption';
import { needTypeOptions } from './consts';
import { AdditionalWorkCheckbox } from './AdditionalWorkCheckbox';

export const NeedTypeSelector = ({ section, onChange, onEditEnd, onCancel }: EditComponentProps<BriefNeedType>) => {
  const { enableEnhancedUx } = useBriefCreationContext();
  const content = section.content || ({} as BriefNeedType);
  const [selectedNeedType, setSelectedNeedType] = useState<Gql.DmBriefNeedType>(
    content.initialNeedType || content.needType
  );
  const [ongoingWorkPotential, setOngoingWorkPotential] = useState<boolean>(Boolean(content.ongoingWorkPotential));

  const shouldAutoSave = enableEnhancedUx && !content.needType;

  const showWarning = useMemo(
    () => !!content.needType && content.needType !== selectedNeedType,
    [content.needType, selectedNeedType]
  );

  const hasAnythingChanged =
    selectedNeedType &&
    (content.needType !== selectedNeedType ||
      (selectedNeedType === 'ONE_TIME' && content.ongoingWorkPotential !== ongoingWorkPotential));

  const [showError, setShowError] = useState(false);

  const handleNeedTypeChange = (newNeedType: Gql.DmBriefNeedType | null) => {
    if (!newNeedType) {
      return;
    }

    setSelectedNeedType(newNeedType);

    if (shouldAutoSave) {
      const newContent: BriefNeedType = {
        needType: newNeedType,
        ongoingWorkPotential,
      };
      onChange(newContent);
      onEditEnd?.();
    }
  };

  const handleOngoingWorkPotentialChange = (checked: boolean) => {
    setOngoingWorkPotential(checked);
  };

  const handleSave = () => {
    if (!content.needType && !hasAnythingChanged) {
      setShowError(true);
      return;
    }

    if (hasAnythingChanged) {
      const newContent: BriefNeedType = {
        needType: selectedNeedType,
        ongoingWorkPotential,
      };
      onChange(newContent);
      setShowError(false);
    }

    onEditEnd?.();
  };

  const handleCancel = () => {
    setSelectedNeedType(content.needType || null);
    setOngoingWorkPotential(content.ongoingWorkPotential || false);
    setShowError(false);
    onCancel?.();
  };

  return (
    <Stack direction="column" paddingBottom={shouldAutoSave ? '2' : undefined}>
      {!enableEnhancedUx && (
        <Typography size="b_sm" color="grey_900">
          <AppI18n k="sections.need_type.subtitle" />
        </Typography>
      )}
      <Stack direction="column" gap="6">
        <ChoiceGroup<Gql.DmBriefNeedType>
          value={selectedNeedType}
          onChange={handleNeedTypeChange}
          marginTop={enableEnhancedUx ? '3' : '6'}
          size="xl"
        >
          {Object.entries(needTypeOptions).map(([needType, option]) => (
            <ChoiceGroupItem<Gql.DmBriefNeedType>
              key={needType}
              value={needType as Gql.DmBriefNeedType}
              flexBasis={0}
              flexGrow={1}
            >
              <NeedTypeOption
                title={appTranslate(option.title)}
                subtitle={appTranslate(option.subtitle)}
                icon={option.icon}
              />
            </ChoiceGroupItem>
          ))}
        </ChoiceGroup>

        {selectedNeedType === 'ONE_TIME' && !enableEnhancedUx && (
          <AdditionalWorkCheckbox checked={ongoingWorkPotential} onChange={handleOngoingWorkPotentialChange} />
        )}

        {showWarning && (
          <Alert variant="warning" showIcon={false}>
            <AlertText>
              <AppI18n k="sections.need_type.change_warning" />
            </AlertText>
          </Alert>
        )}

        {showError && !content.needType && !hasAnythingChanged && (
          <Typography size="b_sm" color="red_900">
            <AppI18n k="sections.need_type.change_error" />
          </Typography>
        )}

        {!shouldAutoSave && (
          <EditActions onCancel={handleCancel} onEditEnd={handleSave} isCancelVisible={!!content.needType} />
        )}
      </Stack>
    </Stack>
  );
};

export default {
  EditComponent: NeedTypeSelector,
};
